import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import FinalGradingForm, { ItemGradingData, FinalGradingSubmitData } from '@/components/quality/FinalGradingForm';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { sendTelegramNotification } from '@/services/telegram/send';
import { format } from 'date-fns';

const QualityFinalGrading: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: procurement, isLoading } = useQuery({
    queryKey: ['procurement-final-grading', id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('procurements')
        .select(`
          *,
          procurement_quality(*),
          farms:farm_id(farmer_name, region),
          items:procurement_items(
            id, product_name, quantity, price_per_unit, quality_grade, notes,
            item_quality:procurement_item_quality(
              id, quality_grade, damage_kg, compensation_kg, damage_percentage,
              preliminary_damage_kg, preliminary_compensation_kg, preliminary_damage_percentage,
              grade_notes, is_rejected
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  const quality = (procurement as any)?.procurement_quality;
  const rawItems = (procurement as any)?.items as Array<any> | undefined;

  // Transform items: item_quality comes as array from Supabase, take first element
  const gradingItems: ItemGradingData[] = (rawItems || []).map(item => {
    const iq = Array.isArray(item.item_quality) ? item.item_quality[0] : item.item_quality;
    return {
      itemId: item.id,
      itemQualityId: iq?.id,
      productName: item.product_name,
      quantity: item.quantity,
      preliminaryDamageKg: iq?.preliminary_damage_kg || 0,
      preliminaryCompensationKg: iq?.preliminary_compensation_kg || 0,
      preliminaryDamagePercentage: iq?.preliminary_damage_percentage || 0,
      qualityGrade: iq?.quality_grade || item.quality_grade,
      isRejected: iq?.is_rejected || false,
    };
  });

  const handleSubmitFinalGrading = async (finalData: FinalGradingSubmitData) => {
    if (!id || !user?.id || !quality?.id) return;

    setIsSubmitting(true);
    try {
      // 1. Upsert per-item quality records
      for (const itemData of finalData.items) {
        if (itemData.itemQualityId) {
          // Update existing item quality record
          const { error } = await supabase
            .from('procurement_item_quality')
            .update({
              damage_kg: itemData.damageKg,
              compensation_kg: itemData.compensationKg,
              damage_percentage: itemData.damagePercentage,
              updated_at: new Date().toISOString(),
            })
            .eq('id', itemData.itemQualityId);
          if (error) throw error;
        } else {
          // Create new item quality record
          const { error } = await supabase
            .from('procurement_item_quality')
            .insert({
              procurement_item_id: itemData.itemId,
              procurement_quality_id: quality.id,
              damage_kg: itemData.damageKg,
              compensation_kg: itemData.compensationKg,
              damage_percentage: itemData.damagePercentage,
              preliminary_damage_kg: 0,
              preliminary_compensation_kg: 0,
              preliminary_damage_percentage: 0,
            });
          if (error) throw error;
        }
      }

      // 2. Update aggregate procurement_quality record
      const { error: qualityError } = await supabase
        .from('procurement_quality')
        .update({
          damage_kg: finalData.totalDamageKg,
          compensation_kg: finalData.totalCompensationKg,
          damage_percentage: finalData.avgDamagePercentage,
          damage_notes: finalData.notes,
          final_graded_at: new Date().toISOString(),
          is_final_grading_complete: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', quality.id);

      if (qualityError) throw qualityError;

      // 3. Send notification
      try {
        const prelimComp = quality.preliminary_compensation_kg || 0;
        const diff = finalData.totalCompensationKg - prelimComp;
        
        let reconciliationInfo = '';
        if (diff !== 0) {
          reconciliationInfo = `\n\n🔄 **Reconciliation Required:**\n` +
            `Preliminary: ${prelimComp} kg → Final: ${finalData.totalCompensationKg} kg\n` +
            `Difference: ${diff > 0 ? '+' : ''}${diff.toFixed(1)} kg`;
        }

        const notificationMessage = `✅ **Final Grading Complete**\n\n` +
          `**Product:** ${procurement?.product_name || 'N/A'}\n` +
          `**Quantity:** ${procurement?.total_quantity || 0} kg\n` +
          `**Items graded:** ${finalData.items.length}\n\n` +
          `**Final Assessment:**\n` +
          `Actual Damage: ${finalData.totalDamageKg} kg\n` +
          `Actual Compensation: ${finalData.totalCompensationKg} kg` +
          reconciliationInfo;

        await sendTelegramNotification(notificationMessage, id);
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }

      toast({
        title: "Final Grading Complete",
        description: "Per-item final grading has been recorded successfully.",
      });

      await queryClient.invalidateQueries({ queryKey: ['procurements'] });
      await queryClient.invalidateQueries({ queryKey: ['quality-pending-final-procurements'] });
      navigate('/quality');
    } catch (error: any) {
      console.error('Error submitting final grading:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit final grading",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!procurement || !quality) {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Procurement not found or no quality record exists.</p>
              <Button className="mt-4" onClick={() => navigate('/quality')}>
                Back to Quality
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (quality.grading_type !== 'preliminary') {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">This procurement does not have preliminary grading.</p>
              <Button className="mt-4" onClick={() => navigate('/quality')}>
                Back to Quality
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (quality.is_final_grading_complete) {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Final grading has already been completed for this procurement.</p>
              <Button className="mt-4" onClick={() => navigate('/quality')}>
                Back to Quality
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/quality')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quality
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold">Complete Final Grading</h1>
          <p className="text-muted-foreground mt-1">
            Enter the actual damage and compensation values per product after complete inspection
          </p>
        </div>

        {/* Procurement Info */}
        <Card>
          <CardHeader>
            <CardTitle>Procurement Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Product</p>
                <p className="font-medium">{procurement.product_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Quantity</p>
                <p className="font-medium">{procurement.total_quantity || 0} kg</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium capitalize">{procurement.status?.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Preliminary Graded</p>
                <p className="font-medium">
                  {quality.preliminary_graded_at 
                    ? format(new Date(quality.preliminary_graded_at), 'MMM dd, yyyy')
                    : 'N/A'}
                </p>
              </div>
            </div>

            {/* Preliminary Assessment Summary */}
            <div className="bg-muted/50 border rounded-lg p-4">
              <h4 className="font-medium mb-3">📋 Preliminary Assessment (Aggregate)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Damage</p>
                  <p className="text-lg font-bold">{quality.preliminary_damage_kg || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Compensation</p>
                  <p className="text-lg font-bold">{quality.preliminary_compensation_kg || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Damage %</p>
                  <p className="font-medium">{quality.preliminary_damage_percentage || 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Est. Net Payable</p>
                  <p className="font-medium">
                    {(procurement.total_quantity || 0) - (quality.preliminary_compensation_kg || 0)} kg
                  </p>
                </div>
              </div>
              {quality.preliminary_notes && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-muted-foreground">Notes:</p>
                  <p className="text-sm">{quality.preliminary_notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Per-Item Final Grading Form */}
        <FinalGradingForm
          items={gradingItems}
          preliminaryData={{
            damageKg: quality.preliminary_damage_kg || 0,
            compensationKg: quality.preliminary_compensation_kg || 0,
            damagePercentage: quality.preliminary_damage_percentage || 0
          }}
          totalQuantity={procurement.total_quantity || 0}
          onSubmit={handleSubmitFinalGrading}
          isSubmitting={isSubmitting}
        />
      </div>
    </Layout>
  );
};

export default QualityFinalGrading;
